// Map Storage Service
// Manages map persistence to localStorage for the map editor
// Allows saving, loading, and managing custom maps

import { PlatformerLevel } from '../../shared/CharacterTypes';
import { serializeGridLevel, deserializeGridLevel } from '../../shared/TileEditorTypes';
import { gameLogger } from '../../utils/GameLogger';
import { LEVEL_STORAGE_CONSTANTS } from '../../shared/constants/GridConstants';

export interface SavedMapInfo {
  id: string;
  name: string;
  savedAt: number;
  isBuiltIn: boolean;
  description?: string;
}

import { LevelRegistry } from '../systems/map/LevelRegistry';
import { GameConstants } from '../../shared/GameConstants';
import { sanitizeGridLevel } from '../../shared/validation/GridLevelValidator';
import { LevelFormatConverter } from '../../shared/LevelFormatConverter';

/**
 * Get the list of built-in map IDs from LevelRegistry
 * Excludes 'dev-map' which is the developer workspace
 */
function getBuiltInMapIds(): string[] {
  const builtInIds = LevelRegistry.getInstance().getAllLevels().map(level => level.id);
  // 'dev-map' is not built-in - it's the developer's workspace
  return builtInIds;
}

/**
 * Check if a level ID is protected (built-in or reserved)
 * 'dev-map' is NOT protected - it's meant to be overwritten during development
 */
function isProtectedLevel(levelId: string): boolean {
  if (levelId === 'dev-map') return false; // Dev workspace is always overwriteable
  if (levelId === GameConstants.Defaults.MAP) return false; // Allow Test map overwrite
  return getBuiltInMapIds().includes(levelId);
}

export class LevelStorageService {
  private static instance: LevelStorageService;

  private constructor() {}

  public static getInstance(): LevelStorageService {
    if (!LevelStorageService.instance) {
      LevelStorageService.instance = new LevelStorageService();
    }
    return LevelStorageService.instance;
  }

  /**
   * Save a level to localStorage
   * Returns the saved level ID (may be different if it was a built-in level)
   *
   * Special handling:
   * - 'dev-map' can always be overwritten (developer workspace)
   * - Built-in levels get renamed to custom_xxx on save
   */
  public saveLevel(level: PlatformerLevel): string | null {
    if (typeof window === 'undefined') return null;
    try {
      let savedId = level.id;

      // Don't allow overwriting protected levels (built-ins)
      // BUT 'dev-map' is NOT protected - it's the developer workspace
      if (isProtectedLevel(level.id)) {
        gameLogger.warn('level', `⚠️ Cannot overwrite built-in level: ${level.id}`);
        // Create a copy with modified ID
        savedId = `custom_${level.id}_${Date.now()}`;
        level = { ...level, id: savedId };
      }

      const key = `${LEVEL_STORAGE_CONSTANTS.STORAGE_PREFIX}${savedId}`;

      // SPARSE: Serialize editorGrid Maps to JSON-compatible format
      let levelToSave = level;
      if (level.editorGrid) {
        levelToSave = {
          ...level,
          editorGrid: serializeGridLevel(level.editorGrid)
        };
      }

      const data = JSON.stringify(levelToSave);
      localStorage.setItem(key, data);

      // Update the level index
      this.updateLevelIndex(savedId, level.name);

      gameLogger.info('level', `✅ Level saved: ${savedId}`);
      return savedId;
    } catch (error) {
      gameLogger.error('level', '❌ Failed to save level:', error);
      return null;
    }
  }

  /**
   * Get the dev-map if it exists
   * This is the developer's workspace that auto-loads in dev mode
   */
  public getDevMap(): PlatformerLevel | null {
    return this.loadLevel('dev-map') as PlatformerLevel | null;
  }

  /**
   * Save current work to the `dev-map` workspace (the developer's last editor
   * work). MUST use the id `'dev-map'` — that's the key every reader expects:
   * the editor Test reload (`?map=dev-map`), `getDevMap()`, and BootService's
   * auto-resume step all read `helix_level_dev-map`. (Previously this saved
   * under `GameConstants.Defaults.MAP` = 'pig-testring', so the workspace key
   * was never written and Test fell back to starter-map.)
   */
  public saveToTestMap(level: PlatformerLevel): boolean {
    const testMap = { ...level, id: 'dev-map', name: 'Dev Workspace' };
    return this.saveLevel(testMap) !== null;
  }

  /**
   * Load a level from localStorage
   * Returns the raw level data (could be GridLevel or PlatformerLevel)
   * The caller (MapSystem) is responsible for converting GridLevel → PlatformerLevel
   */
  public loadLevel(levelId: string): any | null {
    if (typeof window === 'undefined') return null;
    try {
      const key = `${LEVEL_STORAGE_CONSTANTS.STORAGE_PREFIX}${levelId}`;
      const data = localStorage.getItem(key);

      if (!data) {
        gameLogger.warn('level', `📂 Level not found in storage: ${levelId}`);
        return null;
      }

      const level = JSON.parse(data);
      gameLogger.info('level', `✅ Level loaded: ${levelId}`);
      gameLogger.info('level', `📊 Level data: platforms=${level.platforms?.length || 0}, entities=${level.entities?.length || 0}`);
      if (level.entities?.length) {
        gameLogger.info('level', `📊 Entity types: ${level.entities.map((e: any) => e.type).join(', ')}`);
      }

      // SPARSE: Deserialize editorGrid Maps from plain objects
      if (level.editorGrid?.layers) {
        level.editorGrid = deserializeGridLevel(level.editorGrid);
        gameLogger.debug('level', `Deserialized GridLevel with sparse storage: ${levelId}`);
      }

      // Log what type of level it is for debugging and apply sanitization if needed
      if (level.layers && Array.isArray(level.layers)) {
        gameLogger.debug('level', `Loaded GridLevel format: ${levelId}`);
        // Sanitize imported GridLevels to fix AI authoring mistakes
        const { level: cleanLevel, fixes } = sanitizeGridLevel(level);
        if (fixes.length > 0) {
          gameLogger.info('level', `Sanitized ${fixes.length} issues in loaded map ${levelId}`);

          // Re-serialize the clean maps so we can deserialize properly
          cleanLevel.layers.forEach(l => {
             if(l.tiles && !(l.tiles instanceof Map)) {
                 l.tiles = deserializeGridLevel({ layers: [l] }).layers[0].tiles;
             }
          });
        }
        return cleanLevel;
      } else if (level.platforms && Array.isArray(level.platforms)) {
        gameLogger.debug('level', `Loaded PlatformerLevel format: ${levelId}`);
      }

      return level;
    } catch (error) {
      gameLogger.error('level', `❌ Failed to load level ${levelId}:`, error);
      return null;
    }
  }

  /**
   * Get list of all available maps (built-in + custom saved)
   * Returns sorted by most recent first, with clear categorization
   */
  public getAvailableMaps(): SavedMapInfo[] {
    const maps: SavedMapInfo[] = [];
    const seenIds = new Set<string>();

    // Built-in level descriptions
    const builtInDescriptions: Record<string, string> = {
      'starter-map': 'The classic starting area. A clean slate for new adventures.',
      'dev-map': 'Your last editor work — saved here by the 🎮 Test button.'
    };

    // Add built-in maps dynamically from LevelRegistry
    LevelRegistry.getInstance().getAllLevels().forEach(level => {
      if (!seenIds.has(level.id)) {
        maps.push({
          id: level.id,
          name: level.name,
          savedAt: 0,
          isBuiltIn: true,
          description: builtInDescriptions[level.id] || 'Official game map'
        });
        seenIds.add(level.id);
      }
    });

    // Add custom saved maps
    if (typeof window !== 'undefined') {
      try {
        const indexData = localStorage.getItem(LEVEL_STORAGE_CONSTANTS.LEVEL_INDEX_KEY);
        if (indexData) {
          const index = JSON.parse(indexData) as Record<string, { name: string; savedAt: number; description?: string }>;
          Object.entries(index)
            .sort((a, b) => b[1].savedAt - a[1].savedAt) // Sort by most recent
            .forEach(([id, info]) => {
            // Skip built-in IDs and already-seen IDs
            if (!getBuiltInMapIds().includes(id) && !seenIds.has(id)) {
              maps.push({
                id,
                name: info.name,
                savedAt: info.savedAt,
                isBuiltIn: false,
                description: info.description || 'Custom created level'
              });
              seenIds.add(id);
            }
          });
      }
    } catch (error) {
      gameLogger.error('level', 'Failed to load map index:', error);
    }
  }

    return maps;
  }

  /**
   * Delete a saved level
   * 'dev-map' can be deleted (it's the developer workspace)
   */
  public deleteLevel(levelId: string): boolean {
    if (isProtectedLevel(levelId)) {
      gameLogger.warn('level', `⚠️ Cannot delete built-in level: ${levelId}`);
      return false;
    }

    if (typeof window === 'undefined') return false;

    try {
      const key = `${LEVEL_STORAGE_CONSTANTS.STORAGE_PREFIX}${levelId}`;
      localStorage.removeItem(key);
      this.removeLevelFromIndex(levelId);
      gameLogger.info('level', `🗑️ Level deleted: ${levelId}`);
      return true;
    } catch (error) {
      gameLogger.error('level', `❌ Failed to delete level ${levelId}:`, error);
      return false;
    }
  }

  /**
   * Export a level as a downloadable JSON file
   */
  public exportLevel(level: PlatformerLevel): void {
    if (typeof window === 'undefined') return;
    try {
      const dataStr = JSON.stringify(level, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `${level.id}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      gameLogger.info('level', `📥 Level exported: ${level.id}.json`);
    } catch (error) {
      gameLogger.error('level', '❌ Failed to export level:', error);
    }
  }

  /**
   * Import a level from a JSON file
   */
  public async importLevel(file: File): Promise<PlatformerLevel | null> {
    if (typeof window === 'undefined') return null;
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          let levelData = JSON.parse(content);
          let level: PlatformerLevel;

          // If the imported file is a GridLevel, sanitize it and convert it
          if (LevelFormatConverter.isGridLevel(levelData) || levelData.layers) {
             const { level: cleanGrid, fixes } = sanitizeGridLevel(levelData);
             if (fixes.length > 0) {
                 gameLogger.info('level', `Sanitized ${fixes.length} issues in imported GridLevel`);
             }

             // The sanitize converts object to maps when needed, but we must make sure it is a proper GridLevel
             cleanGrid.layers.forEach((l: any) => {
                 if(l.tiles && !(l.tiles instanceof Map)) {
                     l.tiles = deserializeGridLevel({ layers: [l] }).layers[0].tiles;
                 }
             });

             level = LevelFormatConverter.gridToPlatformer(cleanGrid);
          } else {
             level = levelData as PlatformerLevel;
             // Validate required fields for PlatformerLevel
             if (!level.id || !level.name || !level.platforms) {
               gameLogger.error('level', '❌ Invalid level format: missing required fields');
               resolve(null);
               return;
             }
          }

          // Save the imported level
          this.saveLevel(level);
          gameLogger.info('level', `📤 Level imported: ${level.name}`);
          resolve(level);
        } catch (error) {
          gameLogger.error('level', '❌ Failed to parse level file:', error);
          resolve(null);
        }
      };

      reader.onerror = () => {
        gameLogger.error('level', '❌ Failed to read file');
        resolve(null);
      };

      reader.readAsText(file);
    });
  }

  /**
   * Check if a level exists in storage
   */
  public levelExists(levelId: string): boolean {
    if (typeof window === 'undefined') return false;
    const key = `${LEVEL_STORAGE_CONSTANTS.STORAGE_PREFIX}${levelId}`;
    return localStorage.getItem(key) !== null;
  }

  // Private helper methods

  private updateLevelIndex(levelId: string, levelName: string, description?: string): void {
    if (typeof window === 'undefined') return;
    try {
      const indexData = localStorage.getItem(LEVEL_STORAGE_CONSTANTS.LEVEL_INDEX_KEY);
      const index = indexData ? JSON.parse(indexData) : {};

      index[levelId] = {
        name: levelName,
        savedAt: Date.now(),
        description: description || index[levelId]?.description || 'Custom created level'
      };

      localStorage.setItem(LEVEL_STORAGE_CONSTANTS.LEVEL_INDEX_KEY, JSON.stringify(index));
    } catch (error) {
      gameLogger.error('level', 'Failed to update level index:', error);
    }
  }

  private removeLevelFromIndex(levelId: string): void {
    if (typeof window === 'undefined') return;
    try {
      const indexData = localStorage.getItem(LEVEL_STORAGE_CONSTANTS.LEVEL_INDEX_KEY);
      if (indexData) {
        const index = JSON.parse(indexData);
        delete index[levelId];
        localStorage.setItem(LEVEL_STORAGE_CONSTANTS.LEVEL_INDEX_KEY, JSON.stringify(index));
      }
    } catch (error) {
      gameLogger.error('level', 'Failed to update level index:', error);
    }
  }
}
